import React, { useState } from 'react';
import { Button, Input, Loader } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { throttle, uniq } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  AccountSubdomainResponseType,
  AccountSubdomainType,
  ConfirmationType,
} from 'shared/types';
import Icon from '../../../../../../shared/components/Icon';
import { FormField } from '../../../../../../shared/styles';
import {
  useAccountSubdomainchange,
  useCheckDomainAvailability,
} from '../../../hooks';
import { Adornment, CustomFormHeader } from '../styles';

type AccountDomainProps = {
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  accountUrl: string | undefined | null;
  accountStatus: 'INACTIVE' | 'ACTIVATED' | 'DEACTIVATED' | null | undefined;
};

const env: string = process.env.APP_ENV || 'production';
const domainName =
  env === 'production'
    ? 'freshsurvey.io'
    : env === 'staging'
    ? 'sta91ng-freshsurvey.com'
    : 'prestaging.sta91ng-freshsurvey.com';
const validDomainRegex = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]+$/;

const AccountDomain = ({
  accountUrl,
  accountStatus,
  setConfirmationData,
}: AccountDomainProps) => {
  const { t } = useTranslation();
  const [isValid, setIsValid] = useState<boolean>(false);
  const [takenNames, setTakenNames] = React.useState<string[]>([]);

  const currentSubdomain = React.useMemo(
    () => accountUrl?.split('.')[0],
    [accountUrl],
  );

  const { mutate: updateSubdomain } = useAccountSubdomainchange({
    callback: (
      isSuccess: any,
      response: any,
      payload: AccountSubdomainType,
    ) => {
      if (isSuccess) {
        const hostname = `https://${payload.subdomain}.${domainName}`;
        localStorage.clear();
        window.location.href = hostname;
        setConfirmationData({});
      } else {
        setConfirmationData((old) => ({
          ...old,
          confirmButtonDisabled: false,
        }));
      }
    },
  });

  const { mutate: checkDomainAvailability, isLoading } =
    useCheckDomainAvailability({
      callback: (
        isSuccess: any,
        response: AccountSubdomainResponseType,
        payload: AccountSubdomainType,
      ) => {
        if (isSuccess) {
          if (response?.available) {
            setIsValid(true);
          } else {
            setError('subdomain', { message: 'Subdomain is already taken' });
            setIsValid(false);
            setTakenNames((old) =>
              uniq([...old, payload.subdomain || ''].filter((v) => !!v)),
            );
          }
        }
      },
    });

  const form = useForm<AccountSubdomainType>({
    mode: 'onChange',
    resolver: yupResolver(
      Yup.object({
        subdomain: Yup.string()
          .trim()
          .min(
            3,
            `${t('forms.err_msg.subdomain_min_char', {
              len: '3',
            })}`,
          )
          .max(
            50,
            `${t('forms.err_msg.subdomain_max_char', {
              len: '50',
            })}`,
          )
          .matches(validDomainRegex, `${t('forms.err_msg.valid_subdomain')}`)
          .required(`${t('forms.err_msg.subdomain_required')}`)
          .test({
            message: 'Error',
            name: 'subdomain',
            test: function (subdomain, context) {
              if (takenNames.includes(subdomain || '')) {
                return context.createError({
                  message: `${t('forms.err_msg.subdomain_already_taken')}`,
                });
              }
              return true;
            },
          }),
      }).required(),
    ),
    defaultValues: {
      subdomain: currentSubdomain,
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = form;

  const subdomain = watch('subdomain');

  const throttled = React.useRef(
    throttle((searchTerm: string, localTakenNames: string[]) => {
      if (
        !searchTerm ||
        searchTerm === currentSubdomain ||
        localTakenNames.includes(searchTerm)
      )
        return;
      checkDomainAvailability({ subdomain: searchTerm });
    }, 500),
  );

  React.useEffect(() => {
    setIsValid(false);
    return throttled.current(subdomain || '', takenNames);
  }, [subdomain]);

  return (
    <FormField>
      <CustomFormHeader className="heading">
        {t('settings.account_info.account_url')}
      </CustomFormHeader>
      <div className="hint">
        <Icon width="11" height="11" glyph="info" />
        &nbsp; {t('settings.account_info.info')}
      </div>
      <div id="account-url" style={{ display: 'flex' }}>
        <Input
          startAdornment={<Adornment position="start">https://</Adornment>}
          endAdornment={<Adornment position="end">{domainName}</Adornment>}
          {...register('subdomain')}
          isErrored={Boolean(errors?.subdomain)}
          errorText={errors?.subdomain?.message}
          defaultValue={currentSubdomain}
          disabled={accountStatus !== 'ACTIVATED'}
        />
        <Button
          inline
          disabled={
            !!errors?.subdomain ||
            !isValid ||
            isLoading ||
            accountStatus !== 'ACTIVATED'
          }
          onClick={(e) => {
            e.preventDefault();
            setConfirmationData({
              id: new Date().toISOString(),
              title: 'Account URL change confirmation',
              className: 'error',
              confirmButtonText: 'Confirm',
              onConfirm: () => {
                setConfirmationData((old) => ({
                  ...old,
                  confirmButtonDisabled: true,
                }));
                handleSubmit(
                  (data: AccountSubdomainType) => {
                    updateSubdomain(data);
                  },
                  (err) => console.log('ERR', err),
                )();
              },
              fill: '#d7461f',
              infoText: 'Do you wish to proceed with the changes?',
              description:
                'Your Accont URL has been modified. You may need to login again.',
              glyph: 'info',
            });
          }}
        >
          {isLoading ? <Loader size="small" /> : `${t('update')}`}
        </Button>
      </div>
    </FormField>
  );
};

export default AccountDomain;
